import { useMemo } from 'react';
import { gql } from 'graphql-request';
import { TABLE_ASSETS } from '../constants/queryKeys';
import useGraphqlQuery from './useGraphqlQuery';
import useSelectedEnv from './useSelectedEnv';

export const getTableAssetsQuery = gql`
  query GetTableAssets {
    tableAssets(limit: 9000) {
      items {
        id
        lastDataModificationTime
        name
        path
        subscribers
        tags
        owners
        isCritical
        materialization
        dbName
        schemaName
        tableName
      }
    }
  }
`;

export type TableAssetsData = {
  id: string;
  lastDataModificationTime?: string;
  isCritical?: boolean;
  materialization?: string;
  owners?: string[];
  tags?: string[];
  name?: string;
  path?: string;
  subscribers?: string[];
  dbName?: string;
  schemaName?: string;
  tableName?: string;
};

export type TableAssetsResponse = {
  tableAssets: {
    items: TableAssetsData[];
  };
};

type Props = {
  enabled?: boolean;
  onError?: () => void;
};

const useTableAssets = (
  { enabled = true, onError }: Props = { enabled: true },
) => {
  const envid = useSelectedEnv();

  const { data, isFetching } = useGraphqlQuery<TableAssetsResponse>({
    query: getTableAssetsQuery,
    queryKey: [TABLE_ASSETS, envid],
    enabled: enabled,
    onError: onError,
  });

  const tableAssetsDataMap = useMemo(
    () =>
      new Map<string, TableAssetsData>(
        data?.tableAssets?.items.map((item: TableAssetsData) => [
          item.id,
          {
            id: item.id,
            lastDataModificationTime: item.lastDataModificationTime || '',
            name: item.name || '',
            path: item.path || '',
            subscribers: item.subscribers || [],
            tags: item.tags || [],
            owners: item.owners || [],
            isCritical: item.isCritical || false,
            materialization: item.materialization || '',
            dbName: item.dbName || '',
            schemaName: item.schemaName || '',
            tableName: item.tableName || '',
          },
        ]) || [],
      ),
    [data],
  );

  return { data: tableAssetsDataMap, isFetching };
};

export default useTableAssets;
